import { store } from '../Redux/Config/configureStore'
import { getCMS, refetchPublicPage, updatePublicPage } from '../Redux/Reducers/CMS'
import { setNotification } from '../Redux/Reducers/notificationHandling'
import { clearPublicPageDataFromRedux, storePublicPageData } from '../Redux/Reducers/publicPageData'
import { request } from '../Redux/Sagas/requests/api'

/** Session-scoped guest email from public locker checkout — reused on login / guest checkout. */
export const PUBLIC_CHECKOUT_CONTACT_EMAIL_KEY = 'locker_public_checkout_contact_email'

/** Fired on `window` after guest checkout email is saved or cleared (same tab). */
export const PUBLIC_CHECKOUT_EMAIL_UPDATED_EVENT = 'locker:publicCheckoutEmailUpdated'

export const getPublicCheckoutContactEmail = () => {
  try {
    if (typeof sessionStorage === 'undefined') return ''
    return sessionStorage.getItem(PUBLIC_CHECKOUT_CONTACT_EMAIL_KEY) || ''
  } catch {
    return ''
  }
}

export const savePublicCheckoutContactEmail = (email) => {
  try {
    if (typeof sessionStorage === 'undefined') return
    const t = typeof email === 'string' ? email.trim() : ''
    if (t) sessionStorage.setItem(PUBLIC_CHECKOUT_CONTACT_EMAIL_KEY, t)
    else sessionStorage.removeItem(PUBLIC_CHECKOUT_CONTACT_EMAIL_KEY)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PUBLIC_CHECKOUT_EMAIL_UPDATED_EVENT, { detail: { email: t } }))
    }
  } catch {
    /* ignore quota / private mode */
  }
}

export const clearPublicCheckoutContactEmail = () => savePublicCheckoutContactEmail('')

/**
 * Decode an email value from a URL query or path segment (e.g. `%40` → `@`, `+` → space).
 * Safe to run after `URLSearchParams#get` (may already be partially decoded).
 */
export const normalizeCheckoutEmailFromUrl = (raw) => {
  if (raw == null) return ''
  let s = String(raw).trim()
  if (!s) return ''
  try {
    s = decodeURIComponent(s.replace(/\+/g, ' '))
  } catch {
    s = String(raw).trim()
  }
  s = s.replace(/%40/gi, '@')
  return s.trim()
}

/**
 * `get-old-booking` may return a raw array or a wrapper object. Always produce a booking array for UI.
 */
export const normalizeGetOldBookingList = (payload) => {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.bookings)) return payload.bookings
  if (Array.isArray(payload.data)) return payload.data
  if (Array.isArray(payload.results)) return payload.results
  return []
}

/**
 * Point Book-again / year-change prefills at this year's copy of the same block.
 * Clears locker selection so the parent picks from the current-year map.
 */
export const remapUnitsToOpenYear = async (units, { domainId, itemDateTimeId } = {}) => {
  const list = Array.isArray(units) ? units : []
  const locationIds = [...new Set(list.map((unit) => unit?.location_id).filter(Boolean))]
  if (!locationIds.length) return list

  const res = await request('map-lockers-for-rebook', 'POST', {
    domain_id: domainId || undefined,
    item_date_time_id: itemDateTimeId || undefined,
    location_ids: locationIds,
  })
  const body = res?.data || {}
  if (!body?.item_date_time_id) return list

  const locations = body.locations || {}
  return list.map((unit) => {
    const mapped = locations[String(unit.location_id)] || locations[unit.location_id]
    return {
      ...unit,
      item_id: null,
      item_id_label: null,
      item_date_time_id: body.item_date_time_id,
      item_date_time_id_label: body.item_date_time_id_label || unit.item_date_time_id_label,
      location_id: mapped?.id || null,
      location_id_label: mapped?.name || null,
    }
  })
}

export const fetchPublicPagesNav = () => {
  const publicPageList = store.getState().CMS.publicPage

  if (publicPageList?.length > 0) return

  request('public-navigations').then((res) => {
    if (res.status === 200) {
      store.dispatch(updatePublicPage(res?.data))
    }
  })
}

export const getPublicPageData = async (page_uuid) => {
  try {
    const publicPageData = store.getState().publicPageData
    const refetchpage = store.getState().CMS.refetchPublicPage

    if (publicPageData[page_uuid] && !refetchpage) {
      return publicPageData[page_uuid]
    } else {
      const res = await request(`get-page/${page_uuid}`)

      if (res && res.status === 200) {
        let newData = publicPageData ? { ...publicPageData } : {}
        newData[page_uuid] = res?.data
        store.dispatch(storePublicPageData(newData))
        store.dispatch(refetchPublicPage(false))
        return res.data
      } else {
        console.error('Failed to fetch public page data:', res.statusText)
        return null
      }
    }
  } catch (error) {
    console.error('Error fetching public page data:', error)
    return null
  }
}

export const removePublicPageDataByUUID = (uuid) => {
  const publicPageData = store.getState().publicPageData
  let newPageData = {
    ...publicPageData,
  }
  delete newPageData[uuid]
  store.dispatch(storePublicPageData(newPageData))
}

export const _validateFormData = (data) => {
  for (let i = 0; i < data.length; i++) {
    if (!data[i].category_id) {
      // store.dispatch(setNotification({ type: 'error', message: 'Please select Student Year Group in Student ' + (i + 1) }))
      return false
    }
    if (!data[i].item_date_time_id) {
      // store.dispatch(setNotification({ type: 'error', message: 'Please select Year Session in Student ' + (i + 1) }))
      return false
    }
    if (!data[i].location_id) {
      // store.dispatch(setNotification({ type: 'error', message: 'Please Select Block in Student ' + (i + 1) }))
      return false
    }
    if (!data[i].item_id) {
      store.dispatch(setNotification({ type: 'error', message: 'Please Select Locker in Student ' + (i + 1) }))
      return false
    }
    if (data[i].hasOwnProperty('questions')) {
      for (let key in data[i].questions) {
        if (typeof data[i].questions[key] === 'object') {
          if (!data[i].questions[key]?.answer) {
            return false
          }
        }
        if (!data[i].questions[key]) return false
      }
    }
  }
  return true
}

export const checkFilterApplied = (searchData, filterData) => {
  const searchKeys = Object.keys(searchData || {})
  const filterKeys = Object.keys(filterData || {})
  if (searchKeys.length === 0 && filterKeys.length === 0) {
    return false
  }

  const hasSearch = searchKeys.some((key) => Boolean(searchData[key]))
  const hasFilter = filterKeys.some((key) => (filterData[key]?.length ?? 0) > 0)
  return hasSearch || hasFilter
}

export const checkSideBarVisible = (parentData, searchData, filterData, searchResultsAvailable) => {
  if (parentData) return true
  else if (!searchResultsAvailable) {
    return false
  } else if (checkFilterApplied(searchData, filterData)) {
    return true
  } else {
    return false
  }
}

export const updatePublicPageCMS = async (payload, toggleModal = () => {}) => {

  request('public/save-cms', 'POST', payload).then((res) => {
    store.dispatch(clearPublicPageDataFromRedux())
    store.dispatch(refetchPublicPage(true))
    store.dispatch(getCMS())
    setTimeout(() => {
      store.dispatch(refetchPublicPage(false))
      toggleModal()
      store.dispatch(setNotification({ type: 'success', message: 'CMS updated successfully!' }))
    }, 1000)

    // const currentPage = navigation.find((item) => window.location.pathname === '/' + item?.route)
    // removePublicPageDataByUUID(currentPage?.page_uuid)
    // setTimeout(() => {
    //   store.dispatch(refetchPublicPage(true))
    // }, 500)
  })
}
